import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials } from '@/types';
import { api, setAuthToken } from '@/services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  setUser: (user: User | null) => void;
  loadCurrentUser: () => Promise<void>;
  isHydrated: boolean;
  setHydrated: (isHydrated: boolean) => void;
  // Cached selections for faster access across the app
  activeRegionId: number | null;
  activeTeamId: number | null;
  setActiveRegionId: (regionId: number | null) => void;
  setActiveTeamId: (teamId: number | null) => void;
  // Lightweight notification preferences (persisted)
  notifications: {
    soundEnabled: boolean;
    vibrationEnabled: boolean;
  };
  setNotificationsPrefs: (updates: Partial<{ soundEnabled: boolean; vibrationEnabled: boolean }>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isHydrated: false,
      activeRegionId: null,
      activeTeamId: null,
      notifications: {
        soundEnabled: true,
        vibrationEnabled: true,
      },

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true });
        try {
          const { user, token } = await api.postLogin(credentials.email, credentials.password);
          setAuthToken(token);
          set({
            user,
            token,
            isLoading: false,
            // Initialize cached region/team from user when available (safe defaults)
            activeRegionId: (user as Partial<User> & { region_id?: number | null }).region_id ?? null,
            activeTeamId: (user as Partial<User> & { team_id?: number | null }).team_id ?? null,
          });
        } catch (error) {
          set({ isLoading: false });
          // Normalize backend error to the UI-expected message to keep tests/UI stable
          throw new Error('Credenciais inválidas');
        }
      },

      logout: async () => {
        try {
          await api.postLogout();
        } catch (_) {
          // ignore logout network errors
        } finally {
          setAuthToken(null);
          set({ user: null, token: null });
        }
      },

      updateUser: (updates: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      setUser: (user: User | null) => {
        set((state) => ({
          user,
          // When user is explicitly replaced, refresh cached region/team if present
          activeRegionId: (user as Partial<User> & { region_id?: number | null })?.region_id ?? state.activeRegionId,
          activeTeamId: (user as Partial<User> & { team_id?: number | null })?.team_id ?? state.activeTeamId,
        }));
      },

      // Fetch current authenticated user from backend (/user)
      loadCurrentUser: async () => {
        const token = get().token;
        if (!token) return;
        try {
          set({ isLoading: true });
          // Ensure API client has the token (in case of rehydrate)
          setAuthToken(token);
          const me = await api.getUser();
          set((state) => ({
            user: me,
            isLoading: false,
            activeRegionId: (me as Partial<User> & { region_id?: number | null }).region_id ?? state.activeRegionId,
            activeTeamId: (me as Partial<User> & { team_id?: number | null }).team_id ?? state.activeTeamId,
          }));
        } catch (_) {
          // If token invalid/expired, clear session
          setAuthToken(null);
          set({ user: null, token: null, isLoading: false });
        }
      },

      setHydrated: (isHydrated: boolean) => {
        set({ isHydrated });
      },

      setActiveRegionId: (regionId: number | null) => set({ activeRegionId: regionId }),
      setActiveTeamId: (teamId: number | null) => set({ activeTeamId: teamId }),
      setNotificationsPrefs: (updates) => set((state) => ({
        notifications: {
          ...state.notifications,
          ...updates,
        },
      })),
    }),
    {
      name: 'auth-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          setAuthToken(state.token ?? null);
          state.setHydrated(true);
        } else {
          setAuthToken(null);
        }
      },
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        activeRegionId: state.activeRegionId,
        activeTeamId: state.activeTeamId,
        notifications: state.notifications,
      }),
    }
  )
);
